import { type Ref, useId } from "react";
import "./loader.css";
import { MARK_STOPS, PETALS } from "./mark-petals.js";

/**
 * The Crux mark, drawn from its own petals so each one can move.
 *
 * `assemble` is the one loader: petals fly in, the mark turns a third, then it
 * vanishes and assembles again. `still` is the mark as the file draws it.
 * `mono` paints it in the text colour, for the one place the gradient cannot
 * sit: inside a filled button, where the blue petals would disappear into the
 * blue.
 */
export type BloomMotion = "still" | "assemble";

export function Bloom({
  size,
  motion = "still",
  tone = "colour",
  className = "",
  svgRef,
}: {
  readonly size: number;
  readonly motion?: BloomMotion;
  readonly tone?: "colour" | "mono";
  readonly className?: string;
  readonly svgRef?: Ref<SVGSVGElement>;
}) {
  // useId carries colons, which a url(#…) reference does not need to meet.
  const gradientId = `bloom-${useId().replaceAll(/[^a-zA-Z0-9]/g, "")}`;
  const fill = tone === "colour" ? `url(#${gradientId})` : "currentColor";

  return (
    <svg
      ref={svgRef}
      className={`bloom bloom-${motion} ${className}`}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
    >
      {tone === "colour" ? (
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="10"
            y1="90"
            x2="90"
            y2="10"
          >
            {MARK_STOPS.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
        </defs>
      ) : null}
      {PETALS.map((petal, index) => (
        <path
          key={petal.angle}
          className={`bloom-petal bloom-petal-${String(index)}`}
          d={petal.d}
          fill={fill}
        />
      ))}
    </svg>
  );
}
